import { Fragment, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Modal, Carousel } from 'antd';

import useI18n from '../../common/hooks/useI18n';

type Props = {
  pictureUrls: string[];
  visible: boolean;
  carouselCurrentIndex: number;
  onCarouselCurrentIndexChange: (index: number) => void;
  onCancel: () => void;
  matterportUrl?: string;
};

const StyledModal = styled(Modal)`
  @media ${(props) => props.theme.breakpoints.smu} {
    width: 88% !important;
  }

  .ant-modal-body {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .ant-modal-footer {
    padding: 23px 24px;
    position: relative;
    text-align: center;
  }
`;

const FlatImage = styled.div<{ url: string; imageHeight: string }>`
  background-image: url(${(props) => props.url});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center center;

  height: ${(props) => props.imageHeight};
  width: 100%;
`;

const Arrow = styled.div<{ $left?: boolean }>`
  position: absolute;
  top: 50%;
  left: ${(props) => (props.$left ? '24px' : 'inherit')};
  right: ${(props) => (!props.$left ? '24px' : 'inherit')};
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  background-image: ${(props) =>
    props.$left
      ? 'url(/images/prev_black.svg)'
      : 'url(/images/next_black.svg)'};
  cursor: pointer;
`;

const EmbedContainer = styled.div`
  position: relative;
  padding-bottom: calc(min(56.25%, 72vh));
  height: 0;
  overflow: hidden;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

const Gallery: React.FC<Props> = ({
  pictureUrls,
  visible,
  carouselCurrentIndex,
  onCarouselCurrentIndexChange,
  onCancel,
  matterportUrl,
}): JSX.Element => {
  const i18n = useI18n();

  const carouselRef = useRef(null);

  const prev = () => {
    if (carouselRef.current) {
      carouselRef.current.prev();
    }
  };

  const next = () => {
    if (carouselRef.current) {
      carouselRef.current.next();
    }
  };

  useEffect(() => {
    if (carouselRef.current) {
      carouselRef.current.goTo(carouselCurrentIndex);
    }
  }, [carouselCurrentIndex]);

  const title = (
    <div>
      <span>
        {carouselCurrentIndex === pictureUrls.length
          ? i18n.t('flatDetail.actions.virtualVisit')
          : i18n.t('flat.pictures')}
      </span>
      {'   '}
      {matterportUrl && carouselCurrentIndex !== pictureUrls.length && (
        <a onClick={() => onCarouselCurrentIndexChange(pictureUrls.length)}>
          {`(${i18n.t('flatDetail.actions.goToVirtualVisit')})`}
        </a>
      )}
    </div>
  );

  return (
    <StyledModal
      title={title}
      open={visible}
      footer={
        <Fragment>
          <Arrow as="div" $left={true} onClick={prev} />
          <span>{`${carouselCurrentIndex + 1} / ${
            pictureUrls.length + (matterportUrl ? 1 : 0)
          }`}</span>
          <Arrow as="div" $left={false} onClick={next} />
        </Fragment>
      }
      centered
      bodyStyle={{ padding: 0 }}
      onCancel={onCancel}
    >
      <Carousel
        dots={false}
        lazyLoad={'progressive'}
        adaptiveHeight={true}
        beforeChange={(_previousIndex, currentIndex) =>
          onCarouselCurrentIndexChange(currentIndex)
        }
        afterChange={(currentIndex) =>
          onCarouselCurrentIndexChange(currentIndex)
        }
        ref={carouselRef}
      >
        {pictureUrls.map((url) => (
          <FlatImage imageHeight={'72vh'} key={url} url={url} />
        ))}
        {matterportUrl && (
          <EmbedContainer>
            <iframe
              src={matterportUrl}
              frameBorder="0"
              allowFullScreen
              allow="xr-spatial-tracking"
            />
          </EmbedContainer>
        )}
      </Carousel>
    </StyledModal>
  );
};

export default Gallery;
